import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, X } from "@phosphor-icons/react";

const SEV_COLOR = {
  danger: "#dc2626",
  warning: "#d97706",
  info: "var(--text-dim)",
};

export default function NotificationBell({ testid = "notif-bell", viewAllHref }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const dismiss = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    setItems(items.filter(i => i.id !== id));
    try { await api.delete(`/notifications/${encodeURIComponent(id)}`); } catch (_e) { load(); }
  };

  const clearAll = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setItems([]);
    try { await api.delete(`/notifications`); } catch (_e) { load(); }
  };

  const alert = items.filter(i => i.severity !== "info").length;

  return (
    <div className="relative" ref={ref}>
      <button data-testid={testid}
        onClick={() => setOpen(o => !o)}
        className="btn-ghost p-2 relative" title="Notifications">
        <Bell size={18} weight="bold" />
        {alert > 0 && (
          <span className="absolute -top-1 -right-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
            style={{ background: "#dc2626", color: "#fff", minWidth: 18, textAlign: "center" }}
            data-testid={`${testid}-count`}>
            {alert}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-[calc(100vw-3rem)] max-w-96 max-h-[70vh] overflow-y-auto z-50"
          style={{ background: "var(--panel)", border: "1px solid var(--hair)",
                   boxShadow: "0 8px 24px rgba(0,0,0,0.15)" }}
          data-testid={`${testid}-panel`}>
          <div className="flex items-center justify-between px-4 py-3"
            style={{ borderBottom: "1px solid var(--hair)" }}>
            <p className="label" style={{ fontWeight: 700 }}>
              Notifications · {items.length}
            </p>
            <div className="flex items-center gap-1">
              {items.length > 0 && (
                <button onClick={clearAll}
                  className="text-xs px-2 py-1 rounded hover:opacity-70"
                  style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}
                  data-testid={`${testid}-clear-all`}>Clear all</button>
              )}
              <button onClick={() => setOpen(false)}
                className="text-xs p-1 rounded hover:opacity-70"
                style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}>
                <X size={12} />
              </button>
            </div>
          </div>
          {items.length === 0 ? (
            <div className="p-6 text-center label" style={{ color: "var(--text-dim)" }}>
              You're all caught up
            </div>
          ) : (
            items.map(n => (
              <div key={n.id}
                className="flex items-start gap-2 px-4 py-3"
                data-testid={`notif-item-${n.id}`}
                style={{ borderBottom: "1px solid var(--hair)", color: "var(--text)" }}>
                <Link to={n.link || "#"} onClick={() => setOpen(false)}
                  className="flex items-start gap-2 flex-1 min-w-0 hover:opacity-80">
                  <span className="mt-1 shrink-0 w-2 h-2 rounded-full"
                    style={{ background: SEV_COLOR[n.severity] || SEV_COLOR.info }} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm" style={{ overflowWrap: "anywhere" }}>{n.title}</p>
                    {n.subtitle && (
                      <p className="text-xs mt-0.5" style={{ color: "var(--text-dim)", overflowWrap: "anywhere" }}>
                        {n.subtitle}
                      </p>
                    )}
                  </div>
                </Link>
                <button onClick={(e) => dismiss(e, n.id)}
                  className="text-xs p-1 rounded hover:opacity-70"
                  style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}
                  data-testid={`notif-dismiss-${n.id}`}
                  title="Dismiss">
                  <X size={11} weight="bold" />
                </button>
              </div>
            ))
          )}
          {viewAllHref && (
            <Link to={viewAllHref}
              onClick={() => setOpen(false)}
              data-testid={`${testid}-view-all`}
              className="block px-4 py-3 text-center font-medium text-sm hover:opacity-80"
              style={{ borderTop: "1px solid var(--hair)", color: "var(--text)" }}>
              View all notifications
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
